import { Dispatch, KeyboardEvent, SetStateAction, SyntheticEvent, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useRecoilValue } from 'recoil';
import BootstrapForm from 'react-bootstrap/Form';
import classNames from 'classnames';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';

import ComboBox from 'components/common/ComboBox/ComboBox';
import Dropdown from 'components/common/Dropdown/Dropdown';
import IconButton from 'components/common/IconButton/IconButton';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import AddEditPrefilterModal from 'components/dashboards/DashboardPage/modals/AddEditPrefilterModal';
import { generateFilterList, generateParamsList } from 'components/dashboards/dashboardData';
import { DashboardJSONType, DashboardParam, FilterConfigType, ParameterConfigType } from 'components/dashboards/types';
import { facetInfoState } from 'components/search/facets.state';
import { searchParamState } from 'components/common/Managers/RouteStateManager.state';

import { useSetSearchParams } from 'hooks/useSetSearchParams';
import { useIsLabEnabled } from 'hooks/useIsLabEnabled';

import { addToLogQueue } from 'common/ScalyrLogger';
import { UrlParams } from 'common/types';
import { numericStringCompare } from 'common/util/string';
import ELEMENT from 'common/element';
import * as K from 'common/constants';

import { DELETE_DASHBOARD_FILTER, GET_CONFIGV2 } from '../queries';

import './DashboardHeaderParams.scss';

const HIDDEN_DISPLAY_OPTION = 'hidden';

type DashboardHeaderParamProps = {
  dashboardName: string;
  dashboardParams: ParameterConfigType[];
  paramValues?: string;
  dashboardFilters?: FilterConfigType[];
  isEditMode: boolean;
  setIsEditMode: Dispatch<SetStateAction<boolean>>;
};

/**
 * Displays a strip of comboboxes that the user can filter results with.
 *
 * @prop dashboardParams An array of parameter choices to display
 */
function DashboardHeaderParams({
  dashboardName,
  dashboardParams,
  paramValues,
  dashboardFilters = [],
  isEditMode,
  setIsEditMode,
}: DashboardHeaderParamProps): JSX.Element {
  const isDashboardFiltersEnabled = useIsLabEnabled(K.LAB.DASHBOARD_FILTERS);
  const [deleteDashboardFilter] = useMutation(DELETE_DASHBOARD_FILTER, {
    refetchQueries: [{ query: GET_CONFIGV2, variables: { name: dashboardName } }],
  });

  const setSearchParams = useSetSearchParams();
  const dashboardFilterParams = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_FILTERS));
  const [showEditIcon, setShowEditIcon] = useState(false);
  const [showAddParamModal, setShowAddParamModal] = useState(false);
  const [showEditFilterModal, setShowEditFilterModal] = useState(-1);
  const [dashboardFiltersState, setDashboardFiltersState] = useState<FilterConfigType[]>(
    generateFilterList(dashboardFilters, dashboardFilterParams),
  );
  const [params, setParams] = useState<DashboardParam[]>(generateParamsList(dashboardParams, paramValues || ''));
  const tabName = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_ACTIVE_TAB));

  const facetInfo = useRecoilValue(facetInfoState);

  useEffect(() => {
    setParams(generateParamsList(dashboardParams, paramValues || ''));
  }, [dashboardParams]);

  useEffect(() => {
    setDashboardFiltersState(generateFilterList(dashboardFilters, dashboardFilterParams));
  }, [dashboardFilters, dashboardFilterParams]);

  const findDisplayValue = (index: number, value: string): string => {
    if (dashboardParams[index]?.labelsAndValues) {
      const list = dashboardParams[index].labelsAndValues;
      const label = list.find((record): boolean => Object.values(record)[0] === value);
      if (label) {
        return Object.keys(label)[0];
      }
    }
    return dashboardParams[index]?.defaultValue || '';
  };

  const findFiltersDisplayValue = (index: number, value: string): string => {
    if (dashboardFilters[index]?.labelsAndValues) {
      const list = dashboardFilters[index].labelsAndValues;
      const label = list.find((record): boolean => Object.values(record)[0] === value);
      if (label) {
        return Object.keys(label)[0];
      }
    }
    return dashboardFilters[index]?.defaultValue || '';
  };

  const updateParams = (index: number, value: string): void => {
    const newList = [...params];
    newList[index].value = value;
    newList[index].display = findDisplayValue(index, value);
    setParams(newList);
  };

  const issueParams = (): void => {
    const urlValues: DashboardJSONType = {};
    params.forEach((param): void => {
      if (param.value !== param.defaultValue) {
        urlValues[param.name] = param.value;
      }
    });
    const urlParams = new URLSearchParams(urlValues).toString();
    setSearchParams({ params: urlParams } as UrlParams);
  };

  const updateFilters = (index: number, value: string): void => {
    const newList = [...dashboardFiltersState];
    newList[index].value = value;
    setDashboardFiltersState(newList);
  };

  const issueFilters = (): void => {
    const urlValues: DashboardJSONType = {};
    dashboardFiltersState.forEach((filterConfig) => {
      if (filterConfig.value !== filterConfig.defaultValue) {
        urlValues[filterConfig.name] = filterConfig.value;
      }
    });
    const urlParams = new URLSearchParams(urlValues).toString();
    setSearchParams({ dashboardFilters: urlParams } as UrlParams);
  };

  const onSelectionChanged = (index: number, value: string): void => {
    updateParams(index, value);
    issueParams();
  };

  const onFilterSelectionChanged = (index: number, value: string): void => {
    updateFilters(index, value);
    issueFilters();
  };

  if (!dashboardParams) {
    return <></>;
  }

  const isTextParam = (parameterConfig: ParameterConfigType): boolean =>
    !!parameterConfig.isTextInput || (!parameterConfig.facet && parameterConfig.labelsAndValues.length === 0);

  const isCustomParam = (parameterConfig: ParameterConfigType): boolean =>
    !parameterConfig.isTextInput && !parameterConfig.facet;

  const isHiddenParam = (parameterConfig: ParameterConfigType): boolean =>
    !!parameterConfig.options && parameterConfig.options.display === HIDDEN_DISPLAY_OPTION;

  const onKeyDown = (_index: number, event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      issueParams();
    }
  };

  const logFacetInputUsage = (value: string) => {
    addToLogQueue({
      _type: 'dashboardFacetInput',
      dashboardName,
      facetValue: value,
      message: `Facet field filter in dashboard ${dashboardName} updated to "${value}"`,
    });
  };

  const keys = dashboardParams.map((_param, index) => `dashboardParam-${index}`);
  const paramsControls = dashboardParams.map((param, index) => {
    if (isHiddenParam(param)) {
      return <></>;
    }

    let control = null;

    if (isTextParam(param)) {
      control = (
        <BootstrapForm.Control
          className="DashboardHeaderParams__Input"
          onChange={(event: SyntheticEvent): void => updateParams(index, (event.target as HTMLInputElement).value)}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>): void => onKeyDown(index, event)}
          data-index={index}
          value={params[index]?.value}
        />
      );
    } else if (param.facet) {
      const facetValues = facetInfo[param.facet]?.values || [];
      const sortedFacetValues = [...facetValues].sort((a, b) => numericStringCompare(a, b));

      const size = 'md' as any; // "md" is an invalid type for Boostrap's control input, however it works and is the correct size for this case
      control = (
        <ComboBox
          className="DashboardHeaderParams__Input"
          value={params[index]?.value}
          onChange={(facetValue) => {
            updateParams(index, facetValue);
          }}
          size={size}
          items={sortedFacetValues}
          onEnter={(facetValue) => {
            issueParams();
            logFacetInputUsage(facetValue);
          }}
          onSelect={(facetValue) => {
            issueParams();
            logFacetInputUsage(facetValue);
          }}
          selectOnEnter
        />
      );
    } else if (isCustomParam(param)) {
      const dropdownSection = param.labelsAndValues.map((labelAndValue) => ({
        text: Object.keys(labelAndValue)[0],
        clickHandler: (): void => onSelectionChanged(index, Object.values(labelAndValue)[0]),
      }));
      let displayValue = findDisplayValue(index, params[index]?.value);
      if (displayValue.startsWith("'")) {
        displayValue = displayValue.replace("'", '').replace("'", ''); // to avoid a ts2339 compile error with replaceAll :{
      }
      control = (
        <Dropdown sections={[dropdownSection]} size="med">
          {displayValue}
        </Dropdown>
      );
    }

    const className = `DashboardHeaderParams__Param ${
      param.isTextInput || param.facet || (!param.facet && param.labelsAndValues.length === 0)
        ? ''
        : 'DashboardHeaderParams__Param--Dropdown'
    }`;
    return (
      <div key={keys[index]} className={className}>
        <BootstrapForm.Label>{param.name}</BootstrapForm.Label>
        {control}
      </div>
    );
  });

  const dashboardKeys = dashboardFilters.map((filter, index) => `dashboardParam-${index}`);
  const dashboardFilterControls = dashboardFilters.map((filterConfig, index) => {
    let control = null;

    if (filterConfig.facet) {
      const facetValues = facetInfo[filterConfig.facet]?.values || [];
      const sortedFacetValues = [...facetValues].sort((a, b) => numericStringCompare(a, b));

      const size = 'md' as any; // "md" is an invalid type for Boostrap's control input, however it works and is the correct size for this case
      control = (
        <ComboBox
          className="DashboardHeaderParams__Input"
          value={dashboardFiltersState[index]?.value || ''}
          onChange={(facetValue) => {
            updateFilters(index, facetValue);
          }}
          size={size}
          items={sortedFacetValues}
          onEnter={(facetValue) => {
            issueFilters();
            logFacetInputUsage(facetValue);
          }}
          onSelect={(facetValue) => {
            issueFilters();
            logFacetInputUsage(facetValue);
          }}
          onClear={() => {
            issueFilters();
          }}
          selectOnEnter
          testId={ELEMENT.dashboardHeaderControlsFacetFilter}
        />
      );
    } else if (filterConfig.labelsAndValues.length > 0) {
      const dropdownSection = filterConfig.labelsAndValues.map((labelAndValue) => ({
        text: Object.keys(labelAndValue)[0],
        clickHandler: (): void => onFilterSelectionChanged(index, Object.values(labelAndValue)[0]),
      }));
      let displayValue = findFiltersDisplayValue(index, dashboardFiltersState[index]?.value || '');
      if (displayValue.startsWith("'")) {
        displayValue = displayValue.replace("'", '').replace("'", ''); // to avoid a ts2339 compile error with replaceAll :{
      }
      control = (
        <Dropdown sections={[dropdownSection]} size="med" testId={ELEMENT.dashboardHeaderControlsCustomFilter}>
          {displayValue}
        </Dropdown>
      );
    }

    const className = `DashboardHeaderParams__Param ${
      filterConfig.facet || (!filterConfig.facet && filterConfig.labelsAndValues.length === 0)
        ? ''
        : 'DashboardHeaderParams__Param--Dropdown'
    }`;
    return (
      <div key={dashboardKeys[index]} className={className}>
        <BootstrapForm.Label>{filterConfig.name}</BootstrapForm.Label>
        {control}
      </div>
    );
  });

  const editParamsControls = dashboardParams.map((param, index) => {
    if (isHiddenParam(param)) {
      return <></>;
    }

    return (
      <div key={keys[index]} className="DashboardHeaderParams__EditParam d-wrap">
        <div className="DashboardHeaderParams__LabelContainer justify-content-start">
          <IconButton tooltip="Parameters must be edited in JSON" icon={<LockIcon fontSize="small" />} />
          <BootstrapForm.Label className="DashboardHeaderParams__ParamLabel--EditMode">
            {param.name}
          </BootstrapForm.Label>
        </div>

        <div className="DashboardHeaderParams__EditParamInput">{param.name}</div>
      </div>
    );
  });

  const editFiltersControls = dashboardFilters.map((filterConfig, index) => (
    <div key={keys[index]} className="DashboardHeaderParams__EditParam d-wrap">
      <div className="DashboardHeaderParams__LabelContainer">
        <BootstrapForm.Label className="DashboardHeaderParams__ParamLabel--EditMode">
          {filterConfig.name}
        </BootstrapForm.Label>
        <span className="d-flex">
          <IconButton
            toolTipPlacement="bottom"
            tooltip={`Edit ${filterConfig.name}`}
            onClick={() => {
              setShowEditFilterModal(index);
            }}
            icon={<EditIcon fontSize="small" />}
            testId={ELEMENT.dashboardHeaderControlsEditButton}
          />
          <IconButton
            toolTipPlacement="bottom"
            className="ml-1"
            tooltip={`Delete ${filterConfig.name}`}
            onClick={() =>
              deleteDashboardFilter({
                variables: {
                  dashboardName,
                  filterIndex: index,
                  tabName,
                },
              })
            }
            icon={<CloseIcon fontSize="small" />}
            testId={ELEMENT.dashboardHeaderControlsDeleteButton}
          />
        </span>
      </div>
      <Button
        onClick={() => {
          setShowEditFilterModal(index);
        }}
        className="DashboardHeaderParams__EditParamInput"
      >
        {filterConfig.name}
      </Button>
    </div>
  ));

  editParamsControls.push(<div className="DashboardHeaderParams__Fields">{editFiltersControls}</div>);

  editParamsControls.push(
    <Button
      key="DashboardHeaderParams__AddFilterButton"
      variant={ButtonVariant.SECONDARY}
      className="DashboardHeaderParams__AddFilterButton"
      onClick={() => setShowAddParamModal(true)}
      testId={ELEMENT.dashboardHeaderControlsAddFilterButton}
    >
      Add Filter
    </Button>,
  );

  editParamsControls.push(
    <Button
      key="DashboardHeaderParams__DoneButton"
      className="DashboardHeaderParams__DoneButton"
      variant={ButtonVariant.PRIMARY}
      onClick={() => setIsEditMode(false)}
      testId={ELEMENT.dashboardHeaderControlsDoneButton}
    >
      Done
    </Button>,
  );

  const editIconClassnames = classNames('DashboardHeaderParams__EditModeButton', {
    'DashboardHeaderParams__EditModeButton--show': showEditIcon,
  });

  const headerParamsContainerClassnames = classNames('DashboardHeaderParams', {
    'DashboardHeaderParams--EditMode': isEditMode,
  });

  return paramsControls || dashboardFilterControls ? (
    <div
      className={headerParamsContainerClassnames}
      onMouseEnter={() => setShowEditIcon(true)}
      onMouseLeave={() => setShowEditIcon(false)}
    >
      <div className="DashboardHeaderParams__Controls">
        {isEditMode ? (
          editParamsControls
        ) : (
          <>
            {paramsControls}
            {dashboardFilterControls}
          </>
        )}
      </div>

      {!isEditMode && isDashboardFiltersEnabled && (
        <IconButton
          className={editIconClassnames}
          tooltip="Edit Filters"
          onClick={() => setIsEditMode(true)}
          icon={<EditIcon fontSize="small" />}
        />
      )}

      {showAddParamModal && (
        <AddEditPrefilterModal
          showModal={showAddParamModal}
          handleClose={() => setShowAddParamModal(false)}
          dashboardName={dashboardName}
        />
      )}

      {showEditFilterModal >= 0 && (
        <AddEditPrefilterModal
          showModal={showEditFilterModal >= 0}
          handleClose={() => setShowEditFilterModal(-1)}
          dashboardName={dashboardName}
          dashboardFilter={dashboardFilters[showEditFilterModal]}
          index={showEditFilterModal}
        />
      )}
    </div>
  ) : (
    <></>
  );
}

export default DashboardHeaderParams;
